import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'antd/lib/button';
import { CloseOutlined } from '@ant-design/icons';

import { WithId } from '@common/typescript/objects/WithId';
import { TableRemoteSorterData } from '@common/react/smart components/Table/TableRemoteSorter';

import { Filters } from '@app/components/UI/Filters/FilterHook';
import { getFilters, onChangeValue } from '@app/components/UI/Filters/FiltersList';
import { FilterIcon, MobileFiltersBar } from '@app/components/UI/Filters/MobileFilters';
import { TotalQuantity } from '@app/components/UI/Filters/TotalQuantity';
import { Collapsible } from '@app/components/UI/Collapsible/Collapsible';

import '@app/scss/ui/filters-component.scss';

const SHOWN_FILTERS_AMOUNT = 3;

interface OwnProps<T extends WithId> {
	filters: Partial<Filters>;
	onChange: (name: string, value: onChangeValue) => void;
	setQueryParams: (i: Partial<Filters>) => void;
	clearQueryParams: () => void;
	onDeselect: (fieldName: string, type: string, id?: number) => void;
	filterNames: Array<keyof Filters>;
	sorterNames?: Array<string>;
	isLoading: boolean;
	totalQuantity?: number;
	sorter?: TableRemoteSorterData<T> | undefined;
	defaultParams?: Partial<Record<keyof Filters, Record<string, unknown>>>;
	customProperties?: Partial<Record<keyof Filters, Record<string, unknown>>>;
	crematoryId?: number;
}

export const FiltersComponent = <T extends WithId>(props: OwnProps<T>):JSX.Element => {
	const {
		filters, onChange, setQueryParams, clearQueryParams,
		onDeselect, filterNames, isLoading, totalQuantity,
		sorter, sorterNames = filterNames, defaultParams, crematoryId, customProperties,
	} = props;
	const { t } = useTranslation('translation', { keyPrefix: 'filters' });
	const handleSubmit = () => setQueryParams(filters);

	const [opened, setOpened] = React.useState<boolean>(false);

	const allFilterFields = getFilters(filters, onChange, onDeselect, handleSubmit, defaultParams, crematoryId, customProperties);
	const filtersQuantity = Object.keys(filters).length;
	const visibleFilterNames = filterNames.filter(name => Boolean(allFilterFields[name]));

	return (
		<>
			<div className="filters-outer-wrap">
				<div className="filters-inner-wrap">
					<div className="search-fields-block">
						<div className="search-fields-block__shown">
							{
								visibleFilterNames
									.slice(0, SHOWN_FILTERS_AMOUNT)
									.map((elm) => (
										<div
											key={elm}
											className="filter-element filter-element--shown"
										>
											{allFilterFields[elm]}
										</div>
									))
							}
						</div>
						{
							filterNames.length > SHOWN_FILTERS_AMOUNT ? (
								<div className="filter-btn-container">
									<Button
										onClick={() => setOpened(!opened)}
										className={`filter-btn${opened ? '--opened' : ''}`}
										style={{ width: '100%' }}
									>
										{opened ? <CloseOutlined /> : <FilterIcon /> }
									</Button>
								</div>
							) : null
						}
					</div>
					<div className="filter-buttons-block">
						<Button
							type="text"
							onClick={() => clearQueryParams()}
							className="btn-reset"
							disabled={!Object.keys(filters).length}
						>
							<span>{t('reset')}</span>
						</Button>

						<Button
							type="primary"
							onClick={handleSubmit}
							loading={isLoading}
						>
							<span>{t('search')}</span>
							{filtersQuantity > 0 && <span className="applied-filters-amount">{filtersQuantity}</span>}
						</Button>
					</div>
				</div>
				<Collapsible
					in={opened}
					id="hidden-filters"
				>
					<div className="search-fields-block__hidden">
						{
							visibleFilterNames
								.slice(SHOWN_FILTERS_AMOUNT)
								.map((elm) => (
									<div
										className="filter-element filter-element--hidden"
										key={elm}
									>
										{allFilterFields[elm]}
									</div>
								))
						}
					</div>
				</Collapsible>
			</div>

			{
				totalQuantity !== undefined
					? (
						<TotalQuantity
							count={totalQuantity}
							withTotalQuantity
						/>
					) : null
			}

			<MobileFiltersBar
				clearQueryParams={clearQueryParams}
				filterNames={visibleFilterNames}
				sorterNames={sorterNames}
				filters={filters}
				onChange={onChange}
				onDeselect={onDeselect}
				setQueryParams={setQueryParams}
				sorter={sorter}
			/>
		</>
	);
};
